import { api } from '@/lib/api';
import type {
  IApiResponse,
  IMetricDefinition,
  IMetricDefinitionVersion,
  IMetricGroup,
  IHowToUseTip,
} from '@skystone/shared';

interface MetricDefinitionsResponse {
  groups: IMetricGroup[];
}

interface MetricVersionsResponse {
  versions: IMetricDefinitionVersion[];
}

export interface UpdateMetricDefinitionPayload {
  name: string;
  tagline: string;
  definition: string;
  howToUseTips: IHowToUseTip[];
  tiers: Array<{
    tierId: string;
    tierOrder: number;
    label: string;
    lowerBound: number | null;
    upperBound: number | null;
    colorToken: 'success' | 'primary' | 'warning' | 'error';
    description: string;
  }>;
  /**
   * Org-wide canonical values for governance metrics (statusCount,
   * issueTypeCompliance). Only sent for metrics whose definition has the
   * field; omitted entirely otherwise so non-governance metric saves are
   * byte-identical to the pre-PRODOPSAI-376 payload.
   */
  canonicalValues?: string[];
  /**
   * Plain-language "how it's calculated" text. Optional and may be empty —
   * blank is stored as NULL server-side (no Management View tooltip).
   */
  calculation?: string;
  /**
   * Plain-language one-line caveat ("Watch out"). Optional and may be empty —
   * blank is stored as NULL server-side (no catalogue callout).
   */
  watchOut?: string;
}

export const metricDefinitionsApi = {
  getAll: async (): Promise<MetricDefinitionsResponse> => {
    const result = await api.get<IApiResponse<MetricDefinitionsResponse>>('/metric-definitions');
    return result.data;
  },

  update: async (metricKey: string, body: UpdateMetricDefinitionPayload): Promise<IMetricDefinition> => {
    const result = await api.put<IApiResponse<IMetricDefinition>>(`/metric-definitions/${metricKey}`, body);
    return result.data;
  },

  reset: async (metricKey: string): Promise<IMetricDefinition> => {
    const result = await api.post<IApiResponse<IMetricDefinition>>(`/metric-definitions/${metricKey}/reset`);
    return result.data;
  },

  getVersions: async (metricKey: string): Promise<IMetricDefinitionVersion[]> => {
    const result = await api.get<IApiResponse<MetricVersionsResponse>>(
      `/metric-definitions/${metricKey}/versions`,
    );
    return result.data.versions;
  },

  restore: async (metricKey: string, version: number): Promise<IMetricDefinition> => {
    const result = await api.post<IApiResponse<IMetricDefinition>>(
      `/metric-definitions/${metricKey}/restore/${version}`,
    );
    return result.data;
  },
};
